import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, TouchableOpacity } from 'react-native';
import { Text, Searchbar, Chip, Card, ActivityIndicator } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const TenantsListScreen = ({ navigation }) => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  const loadTenants = async () => {
    try {
      const params = [];
      if (search) params.push(`buscar=${encodeURIComponent(search)}`);
      if (filtroEstado !== 'todos') params.push(`estado=${filtroEstado}`);
      const data = await api.superAdmin.getTenants(params.join('&'));
      setTenants(Array.isArray(data) ? data : data.tenants || []);
    } catch (error) {
      console.error('Error cargando tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, [filtroEstado]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      loadTenants();
    });
    return unsubscribe;
  }, [navigation]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadTenants();
    setRefreshing(false);
  }, [search, filtroEstado]);

  const handleSearch = () => {
    setLoading(true);
    loadTenants();
  };

  const getEstadoColor = (estado) => {
    const colors = {
      activo: '#4CAF50',
      pendiente: '#FF9800',
      suspendido: '#f44336',
      cancelado: '#9E9E9E',
    };
    return colors[estado] || '#9E9E9E';
  };

  const renderTenant = ({ item }) => (
    <TouchableOpacity onPress={() => navigation.navigate('TenantDetalle', { tenantId: item.id })}>
      <Card style={styles.tenantCard}>
        <Card.Content>
          <View style={styles.tenantHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.tenantName}>{item.nombre}</Text>
              <Text style={styles.tenantSlug}>{item.slug}</Text>
            </View>
            <Chip
              style={{ backgroundColor: getEstadoColor(item.estado) + '20' }}
              textStyle={{ fontSize: 11, color: getEstadoColor(item.estado) }}
            >
              {item.estado}
            </Chip>
          </View>

          <View style={styles.tenantInfo}>
            <View style={styles.infoItem}>
              <Ionicons name="mail-outline" size={14} color="#888" />
              <Text style={styles.infoText}>{item.email_contacto || '-'}</Text>
            </View>
            {item.plan_nombre && (
              <View style={styles.infoItem}>
                <Ionicons name="pricetag-outline" size={14} color="#888" />
                <Text style={styles.infoText}>Plan: {item.plan_nombre}</Text>
              </View>
            )}
            <View style={styles.infoItem}>
              <Ionicons name="calendar-outline" size={14} color="#888" />
              <Text style={styles.infoText}>
                {item.created_at ? new Date(item.created_at).toLocaleDateString('es-HN') : '-'}
              </Text>
            </View>
          </View>

          {(item.total_usuarios !== undefined || item.total_productos !== undefined) && (
            <View style={styles.statsRow}>
              <View style={styles.statItem}>
                <Text style={styles.statValue}>{item.total_usuarios || 0}</Text>
                <Text style={styles.statLabel}>Usuarios</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statValue}>{item.total_productos || 0}</Text>
                <Text style={styles.statLabel}>Productos</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={styles.statValue}>{item.total_ventas || 0}</Text>
                <Text style={styles.statLabel}>Ventas</Text>
              </View>
            </View>
          )}
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );

  if (loading && tenants.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Buscar empresa..."
        onChangeText={setSearch}
        value={search}
        onSubmitEditing={handleSearch}
        style={styles.searchbar}
      />

      <View style={styles.filtrosRow}>
        {['todos', 'activo', 'pendiente', 'suspendido'].map((estado) => (
          <Chip
            key={estado}
            selected={filtroEstado === estado}
            onPress={() => { setFiltroEstado(estado); setLoading(true); }}
            style={[styles.filtroChip, filtroEstado === estado && styles.filtroChipActive]}
            textStyle={filtroEstado === estado ? { color: '#fff' } : {}}
          >
            {estado.charAt(0).toUpperCase() + estado.slice(1)}
          </Chip>
        ))}
      </View>

      <FlatList
        data={tenants}
        renderItem={renderTenant}
        keyExtractor={(item) => String(item.id)}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="business-outline" size={48} color="#ccc" />
            <Text style={styles.emptyText}>No se encontraron empresas</Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  searchbar: { margin: 12, marginBottom: 8, backgroundColor: 'white', elevation: 1 },
  filtrosRow: { flexDirection: 'row', paddingHorizontal: 12, gap: 8, marginBottom: 8 },
  filtroChip: { backgroundColor: 'white' },
  filtroChipActive: { backgroundColor: '#007bff' },
  listContent: { padding: 12, paddingTop: 4 },
  tenantCard: { marginBottom: 10, backgroundColor: 'white' },
  tenantHeader: { flexDirection: 'row', alignItems: 'center' },
  tenantName: { fontSize: 16, fontWeight: '600', color: '#333' },
  tenantSlug: { fontSize: 12, color: '#007bff', marginTop: 2 },
  tenantInfo: { marginTop: 10 },
  infoItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  infoText: { fontSize: 13, color: '#666', marginLeft: 6 },
  statsRow: { flexDirection: 'row', marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  statItem: { flex: 1, alignItems: 'center' },
  statValue: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  statLabel: { fontSize: 11, color: '#888', marginTop: 2 },
  emptyContainer: { alignItems: 'center', paddingVertical: 40 },
  emptyText: { fontSize: 15, color: '#999', marginTop: 12 },
});

export default TenantsListScreen;
